/**
 * The side-effecting half of the transfer pool (ADR-0027 §5).
 *
 * `transfers.ts` owns the *rule* — which queued ids may start — and is pure so
 * it can be driven without a browser. This owns the *doing*: the jobs, their
 * abort controllers, and the one mutex. Split that way because a job closes
 * over a `File` or a `FileSystemFileHandle`, which is a DOM object, and a
 * registry of them in `transfers.ts` would cost that file the property it was
 * separated for.
 *
 * # Why `started` is a ref and not derived from the list
 *
 * The admission rule is "run N, never two of the same id". `Transfer[]` cannot
 * express it on its own, because dispatching a state change is asynchronous
 * relative to the effect that admits: between `job(signal)` being called and
 * the `hashing` event landing in the reduced list, the row is still `queued`.
 * A second render in that window — and there are several, the clock ticks every
 * second — would admit it again, and the file would upload twice.
 *
 * So the set of launched ids lives in a ref, mutated synchronously at the moment
 * of launch. JavaScript is single-threaded, so a check-and-set in a ref is
 * atomic in the only sense that matters here; a check against state is not.
 *
 * # Why finishes are serialised
 *
 * A finish is a whole-file `sha256sum` on the host, on the buffered SSH path,
 * under a 60-second command timeout. Three of those contending for one disk is
 * how a row fails *after* every byte arrived — the worst failure a transfer
 * queue can produce, because it looks like the transfer was wasted. The gate
 * costs nothing: a finish is short relative to a send.
 */

import { useCallback, useEffect, useRef } from 'react'

import { admit, isActive, type Transfer } from './transfers'

/** A running transfer. Resolves when it has settled, however it settled — the
 *  job dispatches its own outcome, so the pool only needs to know it is over. */
export type TransferJob = (signal: AbortSignal) => Promise<unknown>

export interface TransferPool {
  /** Registers a job. The caller dispatches `queued` for the same id; the pool
   *  starts it when the budget allows. */
  enqueue: (id: string, job: TransferJob) => void
  /** Stops one. A started job is aborted; a queued one never starts. Either way
   *  the caller is told to mark it cancelled, because only it holds the row. */
  cancel: (id: string) => void
  cancelAll: () => void
  /** Serialises whatever it is given against every other caller. */
  gate: <T>(fn: () => Promise<T>) => Promise<T>
}

export function useTransferPool(
  transfers: Transfer[],
  limit: number,
  onCancel: (id: string) => void,
  onDrain: () => void,
): TransferPool {
  const jobs = useRef(new Map<string, TransferJob>())
  const started = useRef(new Set<string>())
  const controllers = useRef(new Map<string, AbortController>())
  const tail = useRef<Promise<unknown>>(Promise.resolve())
  /** True while there is anything to drain, so the trailing refresh fires once
   *  on the edge rather than on every render where nothing is running. */
  const wasBusy = useRef(false)

  // Kept in refs so the admitting effect does not re-run — and re-admit —
  // every time a caller passes a fresh closure.
  const cancelRef = useRef(onCancel)
  const drainRef = useRef(onDrain)
  cancelRef.current = onCancel
  drainRef.current = onDrain

  const enqueue = useCallback((id: string, job: TransferJob) => {
    jobs.current.set(id, job)
  }, [])

  const cancel = useCallback((id: string) => {
    const ctrl = controllers.current.get(id)
    if (ctrl) {
      ctrl.abort()
      // Not deleted here: the job's own `finally` clears it, and deleting now
      // would let the same id be admitted again while its abort is still
      // unwinding.
    } else {
      jobs.current.delete(id)
    }
    cancelRef.current(id)
  }, [])

  const cancelAll = useCallback(() => {
    for (const t of transfers) if (isActive(t)) cancel(t.id)
  }, [transfers, cancel])

  const gate = useCallback(<T,>(fn: () => Promise<T>): Promise<T> => {
    // `then(fn, fn)` rather than `then(fn)`: a rejected predecessor must not
    // wedge the queue for everything behind it.
    const run = tail.current.then(fn, fn) as Promise<T>
    tail.current = run.then(
      () => undefined,
      () => undefined,
    )
    return run
  }, [])

  useEffect(() => {
    for (const id of admit(transfers, limit, started.current)) {
      const job = jobs.current.get(id)
      if (!job) continue
      started.current.add(id)
      const ctrl = new AbortController()
      controllers.current.set(id, ctrl)
      void job(ctrl.signal).finally(() => {
        started.current.delete(id)
        // Identity-checked, because an id can be registered again before the
        // job that holds it has finished unwinding — cancel the transfer, queue
        // the same file, and `enqueue` has already written a *new* job under
        // that id. Deleting by id alone threw that one away, leaving a row that
        // said `Sırada` forever with nothing that would ever start it.
        if (controllers.current.get(id) === ctrl) controllers.current.delete(id)
        if (jobs.current.get(id) === job) jobs.current.delete(id)
      })
    }

    const busy = transfers.some(isActive) || started.current.size > 0
    if (wasBusy.current && !busy) {
      // One refetch when the queue empties, not one per file. With three workers
      // on a two-hundred-file folder the per-file version was two hundred
      // invalidations, each refetching a listing over a fresh connection to the
      // host the transfer had just finished loading.
      drainRef.current()
    }
    wasBusy.current = busy
  }, [transfers, limit])

  // Leaving the screen mid-transfer aborts what is running rather than letting
  // it write into a component nobody is looking at. The partial file stays on
  // the host either way, which is what makes it resumable.
  useEffect(() => {
    const live = controllers.current
    return () => {
      for (const ctrl of live.values()) ctrl.abort()
      live.clear()
    }
  }, [])

  return { enqueue, cancel, cancelAll, gate }
}
